package fr.frux.feature.map

import fr.frux.core.maps.Location
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update

/**
 * Un arbre vient d'être créé (#129 lot 2) : ce que la carte doit dire et montrer au retour.
 *
 * @param treeId Arbre créé, ouvert par l'action « Voir » de la snackbar.
 * @param firstHereConfirmation Message à préférer quand la zone était vide avant l'ajout
 *   (#502) : fait individuel, sans compteur ni comparaison (ADR 0020).
 * @param firstContribution Premier arbre de la personne (spec onboarding de contribution §4) :
 *   la feuille riche remplace la snackbar, avec la pastille si la zone était vide.
 * @param photoUploadFailed La photo reste à renvoyer depuis la fiche, dit dans la feuille.
 * @param zoom Zoom de la visée, gardé après la création plutôt que ramené à celui d'une sélection (#629).
 */
data class NewTreeFocus(
    val target: Location,
    val treeId: Long,
    val confirmation: String? = null,
    val firstHereConfirmation: String? = null,
    val firstContribution: Boolean = false,
    val photoUploadFailed: Boolean = false,
    /** Zoom à garder (celui de la visée) ; `null` = zoom de sélection d'arbre. */
    val zoom: Float? = null,
)

/**
 * Confirmation d'un ajout d'arbre (spec onboarding de contribution §4), sortie de [MapViewModel]
 * (ratchet detekt `LargeClass`). Le premier arbre ouvre une feuille riche à la place du snackbar.
 */
internal class FirstContributionController(
    private val state: MutableStateFlow<MapState>,
) {
    /**
     * Pose le message ou la feuille de confirmation. [wasEmptyHere] est lu avant le rechargement,
     * qui effacera `emptyArea`.
     */
    fun announce(
        wasEmptyHere: Boolean,
        focus: NewTreeFocus,
    ) {
        val celebration =
            if (focus.firstContribution) {
                FirstContributionCelebration(firstHere = wasEmptyHere, photoUploadFailed = focus.photoUploadFailed)
            } else {
                null
            }
        val message =
            when {
                celebration != null -> null
                wasEmptyHere && focus.firstHereConfirmation != null -> focus.firstHereConfirmation
                else -> focus.confirmation
            }
        state.update {
            it.copy(
                confirmationMessage = message,
                confirmationTreeId = focus.treeId.takeIf { message != null },
                firstContribution = celebration,
                canSearchArea = false,
            )
        }
    }

    /**
     * Un arbre vient d'être supprimé depuis sa fiche : annonce sans action (rien à rouvrir) et
     * marqueur retiré tout de suite, sans attendre le rechargement du lot.
     */
    fun announceRemoval(
        treeId: Long,
        message: String,
    ) = state.update {
        it.copy(
            confirmationMessage = message,
            confirmationTreeId = null,
            nearbyTrees = it.nearbyTrees.filterNot { tree -> tree.id == treeId },
            selectedTreeId = it.selectedTreeId.takeIf { selected -> selected != treeId },
        )
    }

    fun consumeConfirmation() = state.update { it.copy(confirmationMessage = null, confirmationTreeId = null) }

    fun consume() {
        state.update { it.copy(firstContribution = null) }
    }
}
